import { CommonModule, DatePipe } from '@angular/common'
import { Component } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { ofType } from '@ngrx/effects'
import { ActionsSubject, Store } from '@ngrx/store'
import { TranslateModule } from '@ngx-translate/core'
import { ConfirmationService } from 'primeng/api'
import { merge } from 'rxjs'
import { $app } from '../../app.service'
import { NavbarService } from '../../core/navbar/navbar.service'
import { AppBaseComponent } from '../../shared/components/app-base.component'
import { AppEditProjectRequest } from '../../shared/models/interfaces/app-edit-project-request.interface'
import { AppProject } from '../../shared/models/interfaces/app-project.interface'
import { AppReportingPeriodRequest } from '../../shared/models/interfaces/app-reporting-period.interface'
import {
   addReportingPeriod,
   addReportingPeriodSuccess,
   deleteLogo,
   deleteLogoSuccess,
   deleteReportingPeriod,
   deleteReportingPeriodSuccess,
   loadLogo,
   loadReportingPeriods,
   updateProjectDetail,
   updateProjectDetailSuccess,
   uploadLogo,
   uploadLogoSuccess
} from '../../shared/store/project/project.actions'
import { selectIsLoadingLogo, selectIsLoadingReportingPeriods, selectLogo, selectReportingPeriods } from '../../shared/store/project/project.selectors'
import { AppState } from '../../shared/store/storage'
import { AppBasicConfirmDialogComponent } from '../../shared/components/app-basic-confirm-dialog/app-basic-confirm-dialog.component'
import { EpProposalComponent } from './ep-proposal/ep-proposal.component'
import { EpRunningComponent } from './ep-running/ep-running.component'

@Component({
   selector: 'feature-edit-project',
   templateUrl: './feature-edit-project.component.html',
   providers: [DatePipe, ConfirmationService],
   standalone: true,
   imports: [CommonModule, EpProposalComponent, EpRunningComponent, AppBasicConfirmDialogComponent, TranslateModule]
})
export class FeatureEditProjectComponent extends AppBaseComponent {
   project$ = $app.getCurrentProject()

   reportingPeriodsRes$ = this._store.select(selectReportingPeriods)
   isLoadingReportingPeriods$ = this._store.select(selectIsLoadingReportingPeriods)

   isLoadingLogo$ = this._store.select(selectIsLoadingLogo)

   projectLogoUrl?: string

   hasUnsavedChanges?: boolean

   tabIndexFromUrl?: string | null = this._route.snapshot.paramMap.get('tabindex')

   constructor(
      private _router: Router,
      private _route: ActivatedRoute,
      private _store: Store<AppState>,
      private _actionsSubj: ActionsSubject,
      private navbarService: NavbarService,
      private _confirmationService: ConfirmationService
   ) {
      super()

      this._router.events.subscribe((event) => {
         if (event instanceof NavigationEnd) {
            const currentTabIndex = event.urlAfterRedirects.split('/edit-project/').pop()
            this.tabIndexFromUrl = currentTabIndex
         }
      })
   }

   ngOnInit() {
      this.initListener()

      delete this.hasUnsavedChanges

      this.listener?.add(
         this._actionsSubj.pipe(ofType(updateProjectDetailSuccess)).subscribe(() => {
            delete this.hasUnsavedChanges
            this._router.navigate([`${$app.getCurrentProjectId()}/dashboard`])
         })
      )

      this.listener?.add(
         merge(this._actionsSubj.pipe(ofType(addReportingPeriodSuccess)), this._actionsSubj.pipe(ofType(deleteReportingPeriodSuccess))).subscribe(() => {
            delete this.hasUnsavedChanges
            this._store.dispatch(loadReportingPeriods())
         })
      )

      this.listener?.add(
         merge(this._actionsSubj.pipe(ofType(uploadLogoSuccess)), this._actionsSubj.pipe(ofType(deleteLogoSuccess))).subscribe(() => {
            delete this.hasUnsavedChanges
            this._store.dispatch(loadLogo())
         })
      )

      if ($app.core.isRunningProject() || $app.core.isEndedProject()) {
         this.listener?.add(
            this.project$.subscribe((project?: AppProject) => {
               if (project) {
                  this._store.dispatch(loadReportingPeriods())
                  this._store.dispatch(loadLogo())
               }
            })
         )

         this.listener?.add(
            this._store.select(selectLogo).subscribe((res?: Blob) => {
               if (res) {
                  this.projectLogoUrl = URL.createObjectURL(res)
               } else {
                  delete this.projectLogoUrl
               }
            })
         )
      }
   }

   doActionUpdateProject(request: AppEditProjectRequest) {
      this._store.dispatch(updateProjectDetail({ request }))
   }

   doActionAddReport(request: AppReportingPeriodRequest) {
      this._store.dispatch(addReportingPeriod({ request }))
   }

   doActionDeleteReport() {
      this._store.dispatch(deleteReportingPeriod())
   }

   doActionSaveLogo(logo: string) {
      this._store.dispatch(uploadLogo({ base64Logo: logo }))
   }

   doActionDeleteLogo() {
      this._store.dispatch(deleteLogo())
   }

   canDeactivate(): Promise<boolean> {
      if (this.hasUnsavedChanges) {
         return new Promise((resolve) => {
            this._confirmationService.confirm(
               $app.core.getLeavePageAlertDialogOptions({
                  acceptFN: () => {
                     delete this.hasUnsavedChanges
                     resolve(true)
                  },
                  rejectFN: () => {
                     this.navbarService.retriveActiveItem()
                     resolve(false)
                  }
               })
            )
         })
      }
      return Promise.resolve(true)
   }
}
